<template>
    <v-dialog
        v-model="show_dialog"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        :persistent="persistent"
        scrollable
        v-bind="$attrs"
    >
        <v-card>
            <v-card-title :class="`${header_bg} ${header_color}--text px-4`">
                <v-icon v-if="icon" dark class="mr-2">{{ icon }}</v-icon>
                <slot name="title">{{ title }}</slot>
            </v-card-title>

            <v-card-text class="pa-0">
                <div v-if="loading" class="loader">
                    <v-progress-circular indeterminate color="primary" />
                </div>

                <div v-else>
                    <slot name="admin">
                        <v-tooltip left>
                            <template #activator="{ on, attrs }">
                                <div v-bind="attrs" v-on="on">
                                    <v-alert
                                        v-if="is_show_system_admin"
                                        type="info"
                                        class="shaded-light ma-3 mb-0"
                                        colored-border
                                        color="primary"
                                        border="left"
                                        dense
                                    >
                                        <template #prepend>
                                            <div></div>
                                        </template>

                                        <div class="pl-2">
                                            <strong>Admin Preview:</strong> You are seeing this dialog because the end user would see it. 
                                            {{ persistent ? `Clicking "${remind_me_later_text}"` : `Dismissing it` }} will <strong>not</strong> prevent the user from seeing it. 
                                            You may complete the dialog on behalf of the user if needed.
                                        </div>
                                    </v-alert>
                                </div>
                            </template>
                            <span>Staff Only</span>
                        </v-tooltip>
                    </slot>
                    
                    <slot />
                </div>
            </v-card-text>

            <v-card-actions v-if="!loading" class="shaded-light">
                <slot name="actions-left" :on_remind="onRemindMeLater">
                    <v-btn
                        text
                        @click="onRemindMeLater"
                    >
                        {{ remind_me_later_text }}
                    </v-btn>
                </slot>

                <v-spacer />

                <slot name="actions-right" :on_complete="onComplete">
                    <v-btn
                        color="primary"
                        :disabled="!can_complete"
                        @click="onComplete"
                    >
                        {{ complete_text }}
                    </v-btn>
                </slot>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
const EVENT_TYPES = {
    SHOW: 'show',
    DISMISS_REMIND_ME_LATER: 'dismiss-remind-me-later',
    COMPLETE: 'complete'
}

const VISIBILITY_RESULTS = {
    DO_NOT_SHOW: 'do-not-show',
    SHOW_USER: 'show-user',
    SHOW_SYSTEM_ADMIN: 'show-system-admin'
}

export default {
    emits: ['shown', 'remind', 'complete'],

    props: {
        notice_type: {
            type: String,
            required: true,
        },
        notice_location: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false
        },
        persistent: {
            type: Boolean,
            default: true
        },
        title: {
            type: String,
            default: ''
        },
        icon: {
            type: String,
            default: null
        },
        header_bg: {
            type: String,
            default: 'primary'
        },
        header_color: {
            type: String,
            default: 'white'
        },
        remind_me_later_text: {
            type: String,
            default: 'Remind Me Later'
        },
        complete_text: {
            type: String,
            default: 'I Understand'
        },
        can_complete: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            show_dialog: false,
            visibility_result: VISIBILITY_RESULTS.DO_NOT_SHOW,
        };
    },

    computed: {
        is_show_user() {
            return this.visibility_result === VISIBILITY_RESULTS.SHOW_USER;
        },

        is_show_system_admin() {
            return this.visibility_result === VISIBILITY_RESULTS.SHOW_SYSTEM_ADMIN;
        }
    },

    methods: {
        getDialogVisibility() {
            this.$rc.rest({
                spinner: false,
                endpoint: "PUT: /api/v1/in-app-notice/get-visibility",
                params: {
                    notice_type: this.notice_type,
                    notice_location: this.notice_location
                },
                on_success: (data) => {


                    this.visibility_result = data.result ?? VISIBILITY_RESULTS.DO_NOT_SHOW;
                    this.show_dialog = this.is_show_user || this.is_show_system_admin;

                    if (this.show_dialog) {
                        this.reportEvent(EVENT_TYPES.SHOW);
                        this.$emit('shown');
                    }
                },
                on_error: (error) => {
                    // fail silently
                }
            });
        },

        reportEvent(event_type) {
            this.$rc.rest({
                spinner: false,
                endpoint: "PUT: /api/v1/in-app-notice/event/:event_type",
                params: {
                    event_type,
                    notice_type: this.notice_type,
                    notice_location: this.notice_location
                },
                on_success: (data) => {
                    // no-op
                },
                on_error: (error) => {
                    // fail silently
                }
            });
        },

        onRemindMeLater() {
            this.reportEvent(EVENT_TYPES.DISMISS_REMIND_ME_LATER);
            this.$emit('remind');
            this.show_dialog = false;
        },

        onComplete() {
            this.reportEvent(EVENT_TYPES.COMPLETE);
            this.$emit('complete');
            this.show_dialog = false;
        },

        devTestReset() {
            if (this.$rc.getEnvironment() !== 'dev') return;

            this.$rc.rest({
                endpoint: "PUT: /api/v1/in-app-notice/dev-test-reset",
                params: {
                    notice_type: this.notice_type
                },
                on_success: () => {
                    this.getDialogVisibility();
                },
                on_error: (error) => {
                }
            });
        },

        // for use by parent components, if ever needed to preserve UX
        // e.g. async content loading failed for some reason
        closeDialog() {
            this.show_dialog = false;
        },
    },

    mounted() {
        // this.devTestReset();
        this.getDialogVisibility();
    },
}
</script>

<style lang="scss" scoped>
    .loader {
        min-height: 20rem;
        display: grid;
        place-items: center;
    }
</style>